import React, { useState } from "react";
import {
    Card, TextField, Typography, Button, Grid, MenuItem,
    Snackbar, Alert, Box, styled, Container
} from "@mui/material";
import { motion } from "framer-motion";

// --- Styled Components (The "Const" Style Toolkit) ---

const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#F8F9FA", // Light professional grey
    padding: theme.spacing(4),
}));

const FormCard = styled(Card)(({ theme }) => ({
    display: "flex",
    flexDirection: "row",
    maxWidth: 900,
    width: "100%",
    borderRadius: "24px",
    overflow: "hidden",
    boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.1)",
    border: "none",
    [theme.breakpoints.down("md")]: {
        flexDirection: "column",
    },
}));

const SideHero = styled(Box)(({ theme }) => ({
    width: "40%",
    backgroundColor: "#09122C",
    padding: theme.spacing(6),
    color: "#FFFFFF",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    [theme.breakpoints.down("md")]: {
        width: "100%",
        padding: theme.spacing(4),
    },
}));

const FormSection = styled(Box)(({ theme }) => ({
    width: "60%",
    padding: theme.spacing(6),
    backgroundColor: "#FFFFFF",
    [theme.breakpoints.down("md")]: {
        width: "100%",
        padding: theme.spacing(4),
    },
}));

const StyledTextField = styled(TextField)({
    "& .MuiOutlinedInput-root": {
        borderRadius: "12px",
        backgroundColor: "#FDFDFD",
        "& fieldset": { borderColor: "#E0E0E0" },
        "&:hover fieldset": { borderColor: "#B8860B" },
        "&.Mui-focused fieldset": { borderColor: "#B8860B" },
    },
    "& .MuiInputLabel-root.Mui-focused": { color: "#B8860B" },
});

const SubmitButton = styled(Button)({
    width: "fit-content", // Fit size requirement
    backgroundColor: "#09122C",
    color: "#FFFFFF",
    fontWeight: 700,
    textTransform: "none",
    padding: "12px 40px",
    borderRadius: "8px",
    fontSize: "1rem",
    marginTop: "24px",
    "&:hover": {
        backgroundColor: "#B8860B",
        color: "#000",
        transform: "translateY(-2px)",
    },
    transition: "all 0.3s ease",
});

// --- Data ---

const planOptions = [
    "Student Plan",
    "Developer Plan",
    "Premium Individual",
    "Bootcamp / School Plan"
];

// --- Main Component ---

export default function PlanApplicationPage() {
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        organization: "",
        plan: ""
    });

    const [submitted, setSubmitted] = useState(false);

    const handleChange = (e) =>
        setFormData({ ...formData, [e.target.name]: e.target.value });

    const handleSubmit = (e) => {
        e.preventDefault();
        console.log("Submitted:", formData);
        setSubmitted(true);
        setFormData({ name: "", email: "", organization: "", plan: "" });
    };

    return (
        <PageWrapper>
            <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                style={{ width: "100%", display: "flex", justifyContent: "center" }}
            >
                <FormCard>
                    {/* Left Branding Panel */}
                    <SideHero>
                        <Typography variant="h6" sx={{ color: "#B8860B", fontWeight: 900, mb: 2 }}>
                            CRYSTAL CAREER
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 800, mb: 3, lineHeight: 1.2 }}>
                            Join a Career Training Plan
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.7, lineHeight: 1.7 }}>
                            Take the first step toward your global professional goals.
                            Our team will review your application and contact you within 24 hours.
                        </Typography>

                        <Box sx={{ mt: "auto", pt: 4 }}>
                            <Typography variant="caption" sx={{ display: "block", opacity: 0.5 }}>
                                OFFICIAL REGISTRATION FORM v2.0
                            </Typography>
                        </Box>
                    </SideHero>

                    {/* Right Form Panel */}
                    <FormSection>
                        <Typography variant="h5" sx={{ fontWeight: 800, color: "#09122C", mb: 1 }}>
                            Registration Details
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#666", mb: 4 }}>
                            Please provide accurate information for verification.
                        </Typography>

                        <form onSubmit={handleSubmit}>
                            <StyledTextField
                                label="Full Name"
                                name="name"
                                value={formData.name}
                                fullWidth
                                onChange={handleChange}
                                margin="normal"
                                required
                            />
                            <StyledTextField
                                label="Email Address"
                                name="email"
                                type="email"
                                value={formData.email}
                                fullWidth
                                onChange={handleChange}
                                margin="normal"
                                required
                            />
                            <StyledTextField
                                label="Organization / School (Optional)"
                                name="organization"
                                value={formData.organization}
                                fullWidth
                                onChange={handleChange}
                                margin="normal"
                            />
                            <StyledTextField
                                select
                                label="Select Your Plan"
                                name="plan"
                                value={formData.plan}
                                fullWidth
                                onChange={handleChange}
                                margin="normal"
                                required
                            >
                                {planOptions.map((option) => (
                                    <MenuItem key={option} value={option}>
                                        {option}
                                    </MenuItem>
                                ))}
                            </StyledTextField>

                            <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
                                <SubmitButton type="submit" variant="contained" disableElevation>
                                    Submit Application
                                </SubmitButton>
                            </Box>
                        </form>
                    </FormSection>
                </FormCard>
            </motion.div>

            <Snackbar
                open={submitted}
                autoHideDuration={4000}
                onClose={() => setSubmitted(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={() => setSubmitted(false)}
                    severity="success"
                    sx={{ width: "100%", borderRadius: "12px", fontWeight: 700 }}
                >
                    Application successfully sent to our admissions team!
                </Alert>
            </Snackbar>
        </PageWrapper>
    );
}
